/**
 * Fire-and-forget (or awaitable) Wikidata → KB hydration with in-flight dedupe by Q-ID.
 *
 * Used when an entity-card is added to a Chart so hydration starts immediately,
 * and by useWikidataAutoFetch so the drawer joins the same request instead of
 * starting a duplicate.
 */

import {getAwpClientHeaders} from '../session/awpRequestHeaders';
import {getAwpApiBase} from './awpApiBase';
import {isLocalEntityRef} from './entityLinks';

const WIKIDATA_ID_RE = /^Q\d+$/i;

/** @type {Map<string, Promise<string>>} */
const inFlight = new Map();

/** @type {Set<() => void>} */
const listeners = new Set();

function notify() {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch {
            /* ignore subscriber errors */
        }
    });
}

/**
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function normalizeWikidataQid(raw) {
    if (raw == null || raw === '') return null;
    const id = String(raw).trim().toUpperCase();
    if (!WIKIDATA_ID_RE.test(id) || isLocalEntityRef(id)) return null;
    return id;
}

/**
 * Subscribe to in-flight import set changes (start / finish).
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeWikidataEntityImports(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * @param {string|null|undefined} qid
 * @returns {boolean}
 */
export function isWikidataEntityImportInFlight(qid) {
    const id = normalizeWikidataQid(qid);
    return id != null && inFlight.has(id);
}

async function postHydrate(qid) {
    const res = await fetch(`${getAwpApiBase()}/api/entities/${encodeURIComponent(qid)}/hydrate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAwpClientHeaders(),
        },
    });
    if (!res.ok) {
        throw new Error(`Hydrate endpoint returned HTTP ${res.status}`);
    }
    const json = await res.json();
    return json.status; // "done" | "skipped" | "not_found"
}

/**
 * POST /api/entities/{qid}/hydrate. Concurrent calls for the same Q-ID share one promise.
 *
 * @param {string|null|undefined} qid
 * @returns {Promise<"done"|"skipped"|"not_found"|"invalid">}
 */
export function importWikidataEntityToKb(qid) {
    const id = normalizeWikidataQid(qid);
    if (!id) {
        return Promise.resolve('invalid');
    }

    const existing = inFlight.get(id);
    if (existing) {
        return existing;
    }

    const promise = postHydrate(id)
        .finally(() => {
            if (inFlight.get(id) === promise) {
                inFlight.delete(id);
                notify();
            }
        });

    inFlight.set(id, promise);
    notify();
    return promise;
}

/** @internal test helper — does not notify subscribers (avoids act() noise after unmount). */
export function __clearWikidataEntityImportInFlightForTests() {
    inFlight.clear();
}
